import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LangSelector from './LangSelector';
const logo = '/icons/icon-192x192.png';
const INSTAGRAM_URL = 'https://www.instagram.com/brooklynstudio.global/';

const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const navLinks = [
    { label: t('brooklyn.nav.projects'), href: '#projetos' },
    { label: t('brooklyn.nav.studio'), href: '#studio' },
    { label: t('brooklyn.nav.curadoria'), href: '#curadoria' },
    { label: t('brooklyn.nav.apply'), href: '/apply', isRoute: true },
  ];

  useEffect(() => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!menuOpen && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setMenuOpen(false);
    if (!isRoute && isHome) {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: 'smooth' });
    } else if (!isRoute && !isHome) {
      navigate('/' + href);
    }
  };

  return (
    <>
      <nav
        role="navigation"
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 w-full z-50 bg-[rgba(8,8,8,0.95)] backdrop-blur-md transition-all duration-400 ${
          scrolled
            ? 'border-b border-border/30'
            : 'border-b border-transparent'
        }`}
      >
            <div className="content-width flex items-center justify-between h-16 px-5 md:px-8">
              <div className="flex items-center gap-3">
                {!isHome && (
                  <button
                    onClick={() => navigate('/')}
                    className="p-2 -ml-2 text-foreground/60 hover:text-foreground transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center"
                    aria-label={t('common.back', 'Go back to home')}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <Link to="/" className="flex items-center gap-3">
                  <img src={logo} alt="Brooklyn Studio" width={32} height={32} className="w-8 h-8 rounded-full object-cover" />
                  <span className="font-display text-lg tracking-[0.1em] text-foreground">
                    BROOKLYN STUDIO
                  </span>
                </Link>
              </div>

              {/* Desktop */}
              <div className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) =>
                  link.isRoute ? (
                    <Link key={link.label} to={link.href} className="nav-link min-h-[48px] flex items-center">
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                      className="nav-link min-h-[48px] flex items-center"
                    >
                      {link.label}
                    </a>
                  )
                )}
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors duration-200 opacity-60 hover:opacity-100 min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Instagram">
                  <Instagram className="w-[18px] h-[18px]" strokeWidth={1.2} />
                </a>
                <LangSelector variant="desktop" />
              </div>

              {/* Mobile: lang + hamburger */}
              <div className="lg:hidden flex items-center gap-1">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors duration-200 opacity-60 hover:opacity-100" aria-label="Instagram">
                  <Instagram className="w-[18px] h-[18px]" strokeWidth={1.2} />
                </a>
                <LangSelector variant="desktop" />
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="w-12 h-12 flex flex-col items-center justify-center gap-[6px]"
                  aria-label={menuOpen ? t('common.close', 'Close menu') : t('common.openMenu', 'Open menu')}
                  aria-expanded={menuOpen}
                >
                <motion.span
                  animate={menuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                  className="block w-6 h-px bg-foreground"
                />
                <motion.span
                  animate={menuOpen ? { opacity: 0 } : { opacity: 1 }}
                  className="block w-6 h-px bg-foreground"
                />
                <motion.span
                  animate={menuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                  className="block w-6 h-px bg-foreground"
                />
              </button>
              </div>
            </div>
      </nav>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-background flex flex-col items-center justify-center gap-10"
            role="dialog"
            aria-label="Navigation menu"
          >
            {navLinks.map((link, i) =>
              link.isRoute ? (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <Link
                    to={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="font-display text-4xl text-foreground tracking-wide min-h-[48px] flex items-center"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ) : (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="font-display text-4xl text-foreground tracking-wide min-h-[48px] flex items-center"
                  >
                    {link.label}
                  </a>
                </motion.div>
              )
            )}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navLinks.length * 0.1, duration: 0.5 }}
            >
              <LangSelector variant="mobile" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
